import SearchBar from "@/components/SearchBar";
import { cn } from "@/lib/utils";
import StatisticsView from "../StatisticsView";
import TagsSection from "./TagsSection";
import TickersSection from "./TickersSection";

export type JournalExplorerContext = "home" | "explore" | "archived" | "profile" | null;

export interface JournalExplorerFeatures {
  search?: boolean;
  statistics?: boolean;
  tags?: boolean;
  tickers?: boolean;
}

interface Props {
  className?: string;
  context?: JournalExplorerContext;
  features?: JournalExplorerFeatures;
  tagCount: Record<string, number>;
  tickerCount: Record<string, number>;
}

const getDefaultFeatures = (context: JournalExplorerContext): JournalExplorerFeatures => {
  switch (context) {
    case null:
      return {
        search: false,
        statistics: false,
        tags: false,
        tickers: false,
      };
    case "explore":
      return {
        search: true,
        statistics: true,
        tags: true,
        tickers: true,
      };
    case "archived":
      return {
        search: true,
        statistics: true,
        tags: true,
        tickers: true,
      };
    case "profile":
      return {
        search: true,
        statistics: true,
        tags: true,
        tickers: true,
      };
    case "home":
    default:
      return {
        search: true,
        statistics: true,
        tags: true,
        tickers: true,
      };
  }
};

const JournalExplorer = (props: Props) => {
  const { className, context = "home", features: featureOverrides = {}, tagCount, tickerCount } = props;

  // Merge default features with overrides
  const features = {
    ...getDefaultFeatures(context),
    ...featureOverrides,
  };

  return (
    <aside
      className={cn(
        "relative w-full h-full overflow-auto flex flex-col justify-start items-start bg-background text-sidebar-foreground",
        className,
      )}
    >
      {features.search && <SearchBar />}
      <div className="mt-1 px-1 w-full space-y-3">
        {features.statistics && <StatisticsView />}
        {features.tickers && (
          <div className="group">
            <div className="glass-card rounded-2xl p-3 transition-all duration-300 hover:shadow-lg hover:shadow-green-500/5 hover:border-green-500/20">
              <TickersSection readonly={context === "explore"} tickerCount={tickerCount} />
            </div>
          </div>
        )}
        {features.tags && (
          <div className="group">
            <div className="glass-card rounded-2xl p-3 transition-all duration-300 hover:shadow-lg hover:shadow-blue-500/5 hover:border-blue-500/20">
              <TagsSection readonly={context === "explore"} tagCount={tagCount} />
            </div>
          </div>
        )}
      </div>
    </aside>
  );
};

export default JournalExplorer;
